import { useState } from 'react';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { applyBackup, type Loaded, type Progress, type Restored } from './restore';
import type { Mode } from './merge';

/**
 * The choice between merge and replace, made after the file has been opened
 * and before anything has been written — so the dialog can say what's in it
 * rather than asking the user to remember.
 */
export function RestoreDialog({ loaded, onClose }: { loaded: Loaded; onClose: () => void }) {
  const [mode, setMode] = useState<Mode>('merge');
  const [progress, setProgress] = useState<Progress | undefined>(undefined);
  const [done, setDone] = useState<Restored | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  const busy = progress !== undefined && done === undefined && error === undefined;
  const counts = loaded.manifest.counts;

  async function restore() {
    setError(undefined);
    setProgress({ phase: 'checking', done: 0, total: counts.images * 2 });

    try {
      setDone(await applyBackup(loaded, mode, setProgress));
    } catch (thrown) {
      setProgress(undefined);
      setError(
        thrown instanceof Error ? thrown.message : "That backup couldn't be restored.",
      );
    }
  }

  if (done !== undefined) {
    return (
      <Dialog title="Restored" onClose={onClose}>
        <p>{summarise(done)}</p>
        {done.orphaned > 0 && (
          <p className="settings__note">
            {done.orphaned} {done.orphaned === 1 ? 'item' : 'items'} named a room that isn’t in the
            file or in VisiList, so {done.orphaned === 1 ? 'it was' : 'they were'} left out.
          </p>
        )}
        <DialogActions>
          <Button onClick={onClose}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog title="Restore From Backup" onClose={busy ? () => undefined : onClose}>
      <p className="settings__note">
        {new Date(loaded.manifest.exportedAt).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
        {' · '}
        {describe(counts)}
        {!loaded.manifest.complete && ' · a selection, not a whole library'}
      </p>

      <div className="choices">
        <Choice
          name="restore-mode"
          value="merge"
          selected={mode}
          disabled={busy}
          onSelect={setMode}
          title="Merge"
          line="Adds anything that’s missing. Nothing already here is changed, so restoring your own backup twice does nothing the second time."
        />
        <Choice
          name="restore-mode"
          value="replace"
          selected={mode}
          disabled={busy}
          onSelect={setMode}
          title="Replace"
          line="Deletes everything in VisiList first, then restores the file. There is no undo."
        />
      </div>

      {busy && progress !== undefined && (
        <p className="settings__progress" role="status">
          {progress.phase === 'checking' ? 'Checking the file' : 'Restoring photos'}
          {progress.total > 0 && ` — ${progress.done} of ${progress.total}`}…
        </p>
      )}

      {error !== undefined && (
        <p className="dialog__error" role="alert">
          {error}
        </p>
      )}

      <DialogActions>
        <Button variant="quiet" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant={mode === 'replace' ? 'destructive' : 'primary'}
          onClick={() => void restore()}
          disabled={busy}
        >
          {mode === 'replace' ? 'Replace Everything' : 'Merge In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Choice({
  name,
  value,
  selected,
  disabled,
  onSelect,
  title,
  line,
}: {
  name: string;
  value: Mode;
  selected: Mode;
  disabled: boolean;
  onSelect: (mode: Mode) => void;
  title: string;
  line: string;
}) {
  return (
    <label className={`choice${selected === value ? ' choice--on' : ''}`}>
      <input
        type="radio"
        name={name}
        checked={selected === value}
        disabled={disabled}
        onChange={() => onSelect(value)}
      />
      <span>
        <span className="choice__title">{title}</span>
        <span className="choice__line">{line}</span>
      </span>
    </label>
  );
}

function describe(counts: { rooms: number; items: number; images: number }): string {
  return [
    plural(counts.rooms, 'room'),
    plural(counts.items, 'item'),
    plural(counts.images, 'photo'),
  ].join(', ');
}

function summarise(restored: Restored): string {
  const { rooms, sections, items, images } = restored.added;
  if (rooms + sections + items + images === 0) {
    return 'Everything in that file was already here. Nothing changed.';
  }
  return `Added ${describe({ rooms, items, images })}.`;
}

function plural(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}
